import { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { Prompt } from 'react-router';
import {
  Box,
  Badge,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  SimpleGrid,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  MdFilterList,
  MdRefresh,
  MdUndo,
  MdSave,
  MdHistory,
  MdHelpOutline,
  MdClose,
} from 'react-icons/md';

import {
  cellValue,
  gridFilters,
  changeCell,
  defaultView,
  displayValue,
  GridColumn,
  GridOption,
  GridDraft,
  GridDrafts,
  GridRow,
  GridValue,
  GridView,
  parseValue,
  readViews,
} from './model';
import { ViewSettings } from './view-settings';
import './grid.css';
import { FilterInput, filterSummary } from './filter-input';

type Props<T extends GridRow> = {
  title: string;
  columns: GridColumn<T>[];
  storageKey: string;
  canEdit: boolean;
  toolbar?: ReactNode;
  load: (view: GridView, page: number) => Promise<{ data: T[]; total: number }>;
  save: (draft: GridDraft<T>) => Promise<void>;
  openForm: (row: T) => void;
};

export function EditableGrid<T extends GridRow>({
  title,
  columns,
  storageKey,
  canEdit,
  toolbar,
  load,
  save,
  openForm,
}: Props<T>) {
  const [store, setStore] = useState(() => readViews(storageKey, columns));
  const [view, setView] = useState<GridView>(
    () =>
      store.views.find((v) => v.id === store.defaultId)?.view ||
      defaultView(columns)
  );
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [drafts, setDrafts] = useState<GridDrafts<T>>({});
  const [selected, setSelected] = useState<Record<string, T>>({});
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [showChanges, setShowChanges] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const filterSearchRef = useRef<HTMLInputElement>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [editing, setEditing] = useState<{
    row: string;
    column: string;
    value: string;
  }>();
  const gridRef = useRef<HTMLTableElement>(null);
  const editorClosed = useRef(false);
  const dirty = Object.keys(drafts).length > 0;
  const visible = useMemo(
    () =>
      view.order
        .map((id) => columns.find((c) => c.id === id))
        .filter((c) => c && !view.hidden.includes(c.id)),
    [columns, view.order, view.hidden]
  );
  const availableFilters = gridFilters(columns);
  const normalizeFilterName = (value: string) =>
    value.trim().toLocaleLowerCase('ru').replace(/ё/g, 'е');
  const matchingFilters = availableFilters.filter((filter) =>
    normalizeFilterName(filter.label).includes(
      normalizeFilterName(filterSearch)
    )
  );
  const editable = columns.filter((c) => c.editable && !c.renderEditor);
  const bulkColumn = editable.find((c) => c.id === bulkField);
  const cellCount = Object.values(drafts).reduce(
    (n, d) => n + Object.keys(d.changes).length,
    0
  );

  useEffect(() => {
    let live = true;
    setLoading(true);
    const timeout = setTimeout(() => {
      load(view, page)
        .then((result) => {
          if (!live) {
            return;
          }
          setRows(result.data);
          setTotal(result.total);
          setError('');
          setLoading(false);
        })
        .catch((e) => {
          if (live) {
            setRows([]);
            setError(e.message || 'Не удалось загрузить таблицу');
            setLoading(false);
          }
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timeout);
    };
  }, [load, view, page, revision]);

  useEffect(() => {
    if (!dirty && !editing) {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, editing]);

  const updateView = (next: GridView) => {
    setView(next);
    setPage(1);
    setEditing(undefined);
  };
  const apply = (
    items: { row: T; column: GridColumn<T>; value: GridValue }[]
  ) => {
    if (!canEdit || savingRef.current) {
      return false;
    }
    const invalid = items
      .map(({ column, value }) => column.validate?.(value))
      .find(Boolean);
    if (invalid) {
      setError(invalid);
      return false;
    }
    setDrafts((previous) =>
      items.reduce(
        (next, item) => changeCell(next, item.row, item.column, item.value),
        previous
      )
    );
    setError('');
    setNotice('');
    return true;
  };
  const commitEditor = () => {
    if (!editing) {
      return true;
    }
    const row = rows.find((r) => String(r.id) === editing.row);
    const column = columns.find((c) => c.id === editing.column);
    if (!row || !column) {
      setEditing(undefined);
      return true;
    }
    if (column.renderEditor) {return false;}
    try {
      if (!apply([{ row, column, value: parseValue(editing.value, column) }])) {
        return false;
      }
      editorClosed.current = true;
      setEditing(undefined);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  };
  const beginEdit = (row: T, column: GridColumn<T>) => {
    if (!canEdit || !column.editable || loading || saving) {
      return;
    }
    if (editing && !commitEditor()) {
      return;
    }
    editorClosed.current = false;
    setEditing({
      row: String(row.id),
      column: column.id,
      value: String(cellValue(row, column, drafts) ?? ''),
    });
  };
  const focus = (r: number, c: number) =>
    gridRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${r}:${c}"]`)
      ?.focus();
  const paste = (text: string, rowIndex: number, columnIndex: number) => {
    try {
      const matrix = text
        .replace(/\r\n/g, '\n')
        .replace(/\n$/, '')
        .split('\n')
        .map((line) => line.split('\t'));
      const items: { row: T; column: GridColumn<T>; value: GridValue }[] = [];
      matrix.forEach((line, r) =>
        line.forEach((value, c) => {
          const row = rows[rowIndex + r];
          const column = visible[columnIndex + c];
          if (!row || !column) {
            throw new Error(
              'Диапазон вставки выходит за пределы текущей страницы.'
            );
          }
          if (!column.editable) {
            throw new Error(
              `${column.label}: поле доступно только для чтения. Вставка отменена.`
            );
          }
          items.push({ row, column, value: parseValue(value, column) });
        })
      );
      apply(items);
    } catch (e) {
      setError(e.message);
    }
  };
  const submit = async () => {
    if (!canEdit || savingRef.current || editing) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setNotice('');
    let success = 0;
    let failed = 0;
    try {
      for (const [id, draft] of Object.entries(drafts)) {
        try {
          await save(draft);
          success++;
          setDrafts((current) => {
            const next = { ...current };
            delete next[id];
            return next;
          });
          setSelected((current) => {
            const next = { ...current };
            delete next[id];
            return next;
          });
        } catch (e) {
          failed++;
          setDrafts((current) => ({
            ...current,
            [id]: {
              ...draft,
              error: e.message || 'Не удалось сохранить запись',
            },
          }));
        }
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      setRevision((n) => n + 1);
      setNotice(`Сохранено: ${success}. Ошибок: ${failed}.`);
      if (failed) {
        setShowChanges(true);
      }
    }
  };
  const filterChange = (id: string, value: string, options?: GridOption[]) =>
    updateView({
      ...view,
      filters: { ...view.filters, [id]: value },
      ...(options
        ? {
            filterLabels: {
              ...view.filterLabels,
              [id]: Object.fromEntries(options.map((o) => [o.value, o.label])),
            },
          }
        : {}),
    });
  const busy = saving || !!editing;
  const selectedRows = Object.values(selected);

  return (
    <Box bg="white" borderRadius="md" p={4}>
      <Prompt
        when={dirty || !!editing || saving}
        message={() =>
          saving
            ? false
            : 'Есть несохранённые изменения. Покинуть страницу и потерять их?'
        }
      />
      <Flex align="center" justify="space-between" gap={3} wrap="wrap" mb={3}>
        <Flex align="center" gap={2}>
          <Text as="h1" fontSize="lg" fontWeight="semibold">
            {title}
          </Text>
          <Badge colorScheme="blue" variant="subtle" textTransform="none">
            Пилот
          </Badge>
        </Flex>
        {toolbar}
      </Flex>
      <Flex
        role="group"
        aria-label="Панель действий таблицы"
        align="center"
        gap={2}
        wrap="wrap"
        bg="gray.50"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        px={3}
        py={2}
        mb={3}
      >
        <ViewSettings
          columns={columns}
          view={view}
          onChange={updateView}
          storageKey={storageKey}
          store={store}
          onStore={setStore}
          disabled={busy}
        />
        <Box
          width="1px"
          alignSelf="stretch"
          bg="gray.200"
          mx={1}
          display={{ base: 'none', md: 'block' }}
        />
        <Button
          size="sm"
          variant={showFilters ? 'solid' : 'ghost'}
          colorScheme={showFilters ? 'blue' : 'gray'}
          leftIcon={<MdFilterList size={18} />}
          aria-expanded={showFilters}
          onClick={() => {
            setFilterSearch('');
            setShowFilters(true);
          }}
        >
          Фильтры
          {Object.values(view.filters).filter(Boolean).length
            ? ` · ${Object.values(view.filters).filter(Boolean).length}`
            : ''}
        </Button>
        <Tooltip label="Обновить данные">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Обновить данные"
            icon={<MdRefresh size={19} />}
            isDisabled={busy || loading}
            onClick={() => setRevision((n) => n + 1)}
          />
        </Tooltip>
        <Tooltip label="Горячие клавиши и подсказки">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Горячие клавиши и подсказки"
            aria-expanded={showHelp}
            icon={<MdHelpOutline size={18} />}
            onClick={() => setShowHelp(!showHelp)}
          />
        </Tooltip>
        <Flex ml={{ base: 0, lg: 'auto' }} align="center" gap={1} wrap="wrap">
          {dirty && (
            <Badge
              colorScheme="orange"
              variant="subtle"
              textTransform="none"
              mr={2}
              title={`Изменено строк: ${
                Object.keys(drafts).length
              } · ячеек: ${cellCount}`}
            >
              {Object.keys(drafts).length} строк · {cellCount} ячеек
            </Badge>
          )}
          <Tooltip label="Показать изменения">
            <IconButton
              size="sm"
              variant={showChanges ? 'solid' : 'ghost'}
              aria-label="Показать изменения"
              aria-expanded={showChanges}
              icon={<MdHistory size={19} />}
              isDisabled={!dirty}
              onClick={() => setShowChanges(!showChanges)}
            />
          </Tooltip>
          <Tooltip label="Отменить все изменения">
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Отменить все изменения"
              icon={<MdUndo size={19} />}
              isDisabled={!dirty || busy}
              onClick={() => setDrafts({})}
            />
          </Tooltip>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<MdSave size={17} />}
            aria-label="Применить изменения"
            isLoading={saving}
            isDisabled={!canEdit || !dirty || !!editing}
            onClick={submit}
          >
            Применить
          </Button>
        </Flex>
      </Flex>
      <Text srOnly aria-live="polite">
        Изменено строк: {Object.keys(drafts).length} · ячеек: {cellCount}
      </Text>
      {canEdit && selectedRows.length > 0 && (
        <Flex
          gap={2}
          wrap="wrap"
          align="center"
          mb={3}
          p={3}
          bg="blue.50"
          borderWidth="1px"
          borderColor="blue.100"
          rounded="md"
          aria-label="Массовое редактирование"
        >
          <Text fontSize="sm">Выбрано: {selectedRows.length}</Text>
          <Button
            size="xs"
            isDisabled={busy || !selectedRows.length}
            onClick={() => {
              setSelected({});
              setSourceId('');
            }}
          >
            Снять выбор
          </Button>
          <Select
            size="sm"
            w="210px"
            aria-label="Поле массового изменения"
            value={bulkField}
            isDisabled={busy}
            onChange={(e) => {
              setBulkField(e.target.value);
              setBulkValue('');
            }}
          >
            <option value="">Поле для изменения</option>
            {editable.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          {['select', 'boolean'].includes(bulkColumn?.type) ? (
            <Select
              size="sm"
              w="200px"
              aria-label="Массовое значение"
              value={bulkValue}
              isDisabled={busy}
              onChange={(e) => setBulkValue(e.target.value)}
            >
              <option value="">Пусто</option>
              {bulkColumn.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              size="sm"
              w="200px"
              aria-label="Массовое значение"
              placeholder="Новое значение"
              type={bulkColumn?.type === 'date' ? 'date' : 'text'}
              value={bulkValue}
              isDisabled={busy}
              onChange={(e) => setBulkValue(e.target.value)}
            />
          )}
          <Button
            size="sm"
            isDisabled={busy || !bulkColumn || !selectedRows.length}
            onClick={() => {
              try {
                apply(
                  selectedRows.map((row) => ({
                    row,
                    column: bulkColumn,
                    value: parseValue(bulkValue, bulkColumn),
                  }))
                );
              } catch (e) {
                setError(e.message);
              }
            }}
          >
            Изменить выбранные ({selectedRows.length})
          </Button>
          <Select
            size="sm"
            w="180px"
            aria-label="Строка-источник"
            value={sourceId}
            isDisabled={busy}
            onChange={(e) => setSourceId(e.target.value)}
          >
            <option value="">Копировать из строки…</option>
            {rows.map((r) => (
              <option key={r.id} value={String(r.id)}>
                № {r.id}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            isDisabled={
              busy ||
              !bulkColumn ||
              !sourceId ||
              !selectedRows.length ||
              !rows.some((r) => String(r.id) === sourceId)
            }
            onClick={() => {
              const source = rows.find((r) => String(r.id) === sourceId);
              if (source) {
                apply(
                  selectedRows
                    .filter((r) => r.id !== source.id)
                    .map((row) => ({
                      row,
                      column: bulkColumn,
                      value: cellValue(source, bulkColumn, drafts),
                    }))
                );
              }
            }}
          >
            Скопировать поле в выбранные
          </Button>
        </Flex>
      )}
      {showHelp && (
        <Text
          fontSize="sm"
          color="gray.600"
          mb={3}
          p={3}
          bg="gray.50"
          rounded="md"
        >
          Двойной щелчок или Enter — редактировать. Escape — отменить ввод.
          Стрелки — переход. Ctrl/Cmd+C — копировать ячейку, Ctrl/Cmd+V —
          вставить диапазон. Фильтры применяются ко всей серверной выборке.
          Черновик сохраняется между страницами до выхода.
        </Text>
      )}
      <Drawer
        isOpen={showFilters}
        initialFocusRef={filterSearchRef}
        onClose={() => setShowFilters(false)}
        placement="right"
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton aria-label="Закрыть фильтры" />
          <DrawerHeader pr={12}>
            Фильтры ({availableFilters.length})
            <InputGroup size="sm" mt={3}>
              <Input
                ref={filterSearchRef}
                aria-label="Поиск фильтра по названию поля"
                placeholder="Найти поле…"
                value={filterSearch}
                onChange={(event) => setFilterSearch(event.target.value)}
                pr={8}
                fontWeight="normal"
              />
              {filterSearch && (
                <InputRightElement>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label="Очистить поиск полей"
                    icon={<MdClose />}
                    onClick={() => {
                      setFilterSearch('');
                      filterSearchRef.current?.focus();
                    }}
                  />
                </InputRightElement>
              )}
            </InputGroup>
          </DrawerHeader>
          <DrawerBody className="grid-all-filters" pb={6}>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Фильтры применяются автоматически. Для справочников используйте
              поиск по названию и отметьте нужные значения. Для диапазона дат
              укажите обе границы.
            </Text>
            {error && (
              <Text role="alert" color="red.700" mb={3}>
                {error}
              </Text>
            )}
            {!matchingFilters.length && (
              <Text role="status" color="gray.500" py={4}>
                Поля не найдены. Попробуйте другое название.
              </Text>
            )}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {matchingFilters.map((filter) => (
                <Box key={filter.id} minW={0}>
                  <Text fontSize="sm" mb={1}>
                    {filter.label}
                  </Text>
                  <FilterInput
                    filter={{
                      ...filter,
                      label: `${filter.label} (все фильтры)`,
                    }}
                    value={view.filters[filter.id] || ''}
                    disabled={busy}
                    labels={view.filterLabels?.[filter.id]}
                    onChange={(value, options) =>
                      filterChange(filter.id, value, options)
                    }
                  />
                </Box>
              ))}
            </SimpleGrid>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" gap={2}>
            <Button
              size="sm"
              variant="ghost"
              isDisabled={busy || !Object.values(view.filters).some(Boolean)}
              onClick={() => updateView({ ...view, filters: {} })}
            >
              Сбросить фильтры
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => setShowFilters(false)}
            >
              Готово
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {Object.entries(view.filters).some(([, value]) => !!value) && (
        <Flex gap={2} wrap="wrap" mb={2} aria-label="Активные фильтры">
          {Object.entries(view.filters)
            .filter(([, value]) => !!value)
            .map(([id, value]) => {
              const column = availableFilters.find((c) => c.id === id);
              return (
                <Button
                  key={id}
                  size="xs"
                  isDisabled={busy}
                  onClick={() => filterChange(id, '')}
                >
                  {column?.label}:{' '}
                  {column
                    ? filterSummary(column, value, view.filterLabels?.[id])
                    : value}{' '}
                  ×
                </Button>
              );
            })}
        </Flex>
      )}
      {error && (
        <Text role="alert" color="red.700" mb={2}>
          {error}
        </Text>
      )}
      {notice && (
        <Text role="status" mb={2}>
          {notice}
        </Text>
      )}
      {showChanges && dirty && (
        <Box borderWidth="1px" p={3} mb={3} maxH="300px" overflow="auto">
          {Object.entries(drafts).map(([id, draft]) => (
            <Box key={id} mb={2}>
              <Flex gap={2} align="center">
                <Text fontWeight="bold">ТТ № {id}</Text>
                <Button
                  size="xs"
                  isDisabled={busy}
                  onClick={() =>
                    setDrafts((current) => {
                      const next = { ...current };
                      delete next[id];
                      return next;
                    })
                  }
                >
                  Отменить строку
                </Button>
              </Flex>
              {Object.entries(draft.changes).map(([field, value]) => {
                const column = columns.find((c) => c.id === field);
                return (
                  <Text key={field} fontSize="sm">
                    {column.label}:{' '}
                    {displayValue(column.getValue(draft.original), column) ||
                      '∅'}{' '}
                    → {displayValue(value, column) || '∅'}
                  </Text>
                );
              })}
              {draft.error && (
                <Text role="alert" color="red.700">
                  {draft.error}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      )}
      {loading && (
        <Flex role="status" align="center" gap={2} mb={2}>
          <Spinner size="sm" />
          Загрузка…
        </Flex>
      )}
      <Box
        className="editable-grid"
        overflow="auto"
        maxH="65vh"
        borderWidth="1px"
        aria-busy={loading}
      >
        <table ref={gridRef} aria-label={title}>
          <thead>
            <tr>
              <th>
                <Checkbox
                  aria-label="Выбрать текущую страницу"
                  isDisabled={!canEdit || loading || busy || !rows.length}
                  isChecked={
                    rows.length > 0 &&
                    rows.every((r) => !!selected[String(r.id)])
                  }
                  isIndeterminate={
                    rows.some((r) => !!selected[String(r.id)]) &&
                    !rows.every((r) => !!selected[String(r.id)])
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelected((current) => {
                      const next = { ...current };
                      rows.forEach((r) => {
                        if (checked) {
                          next[String(r.id)] = r;
                        } else {
                          delete next[String(r.id)];
                        }
                      });
                      return next;
                    });
                  }}
                />
              </th>
              {visible.map((c) => (
                <th
                  key={c.id}
                  aria-sort={
                    view.sort.column === c.id
                      ? view.sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <button
                    className="grid-heading"
                    disabled={!c.sortable || busy}
                    onClick={() =>
                      updateView({
                        ...view,
                        sort: {
                          column: c.id,
                          direction:
                            view.sort.column === c.id &&
                            view.sort.direction === 'asc'
                              ? 'desc'
                              : 'asc',
                        },
                      })
                    }
                  >
                    {c.label}
                    {view.sort.column === c.id
                      ? view.sort.direction === 'asc'
                        ? ' ↑'
                        : ' ↓'
                      : ''}
                  </button>
                  {c.filterable && (
                    <FilterInput
                      filter={c}
                      value={view.filters[c.id] || ''}
                      disabled={busy}
                      labels={view.filterLabels?.[c.id]}
                      onChange={(value, options) =>
                        filterChange(c.id, value, options)
                      }
                    />
                  )}
                </th>
              ))}
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row.id}>
                <td>
                  <Checkbox
                    aria-label={`Выбрать ТТ ${row.id}`}
                    isDisabled={!canEdit || loading || busy}
                    isChecked={!!selected[String(row.id)]}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelected((current) => {
                        const next = { ...current };
                        if (checked) {
                          next[String(row.id)] = row;
                        } else {
                          delete next[String(row.id)];
                        }
                        return next;
                      });
                    }}
                  />
                </td>
                {visible.map((column, c) => {
                  const value = cellValue(row, column, drafts);
                  const active =
                    editing?.row === String(row.id) &&
                    editing.column === column.id;
                  const changed = Object.prototype.hasOwnProperty.call(
                    drafts[String(row.id)]?.changes || {},
                    column.id
                  );
                  const editorProps = {
                    autoFocus: true,
                    'aria-label': `Изменить ${column.label}, ТТ ${row.id}`,
                    value: editing?.value || '',
                    onChange: (e) =>
                      setEditing({ ...editing, value: e.target.value }),
                    onBlur: () => {
                      if (!editorClosed.current) {
                        commitEditor();
                      }
                    },
                    onKeyDown: (e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        editorClosed.current = true;
                        setEditing(undefined);
                        focus(r, c);
                      }
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        if (commitEditor()) {
                          focus(
                            e.key === 'Enter'
                              ? Math.min(r + 1, rows.length - 1)
                              : r,
                            e.key === 'Tab'
                              ? Math.max(
                                  0,
                                  Math.min(
                                    c + (e.shiftKey ? -1 : 1),
                                    visible.length - 1
                                  )
                                )
                              : c
                          );
                        }
                      }
                    },
                  };
                  return (
                    <td
                      key={column.id}
                      data-cell={`${r}:${c}`}
                      tabIndex={0}
                      className={
                        changed
                          ? 'grid-changed'
                          : !column.editable
                          ? 'grid-readonly'
                          : ''
                      }
                      aria-label={`${column.label}, ТТ ${row.id}`}
                      onDoubleClick={() => beginEdit(row, column)}
                      onKeyDown={(e) => {
                        if (active) {
                          return;
                        }
                        if (['Enter', 'F2'].includes(e.key)) {
                          e.preventDefault();
                          beginEdit(row, column);
                        }
                        const moves = {
                          ArrowUp: [-1, 0],
                          ArrowDown: [1, 0],
                          ArrowLeft: [0, -1],
                          ArrowRight: [0, 1],
                        };
                        if (moves[e.key]) {
                          e.preventDefault();
                          const [dr, dc] = moves[e.key];
                          focus(r + dr, c + dc);
                        }
                      }}
                      onCopy={(e) => {
                        if (!active) {
                          e.preventDefault();
                          e.clipboardData.setData(
                            'text/plain',
                            displayValue(value, column)
                          );
                        }
                      }}
                      onPaste={(e) => {
                        if (!active && canEdit && !loading && !saving) {
                          e.preventDefault();
                          paste(e.clipboardData.getData('text/plain'), r, c);
                        }
                      }}
                    >
                      {active ? (
                        column.renderEditor ? (
                          column.renderEditor({
                            row,
                            value: editing.value,
                            onSave: (value) => {
                              if (apply([{ row, column, value }])) {
                                editorClosed.current = true;
                                setEditing(undefined);
                              }
                            },
                            onCancel: () => {
                              editorClosed.current = true;
                              setEditing(undefined);
                            },
                          })
                        ) : ['select', 'boolean'].includes(column.type) ? (
                          <select {...editorProps}>
                            <option value="">Пусто</option>
                            {column.options?.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            {...editorProps}
                            type={column.type === 'date' ? 'date' : 'text'}
                            inputMode={
                              column.type === 'number' ? 'decimal' : undefined
                            }
                          />
                        )
                      ) : (
                        displayValue(value, column) || '—'
                      )}
                    </td>
                  );
                })}
                <td>
                  <Button
                    size="xs"
                    isDisabled={!canEdit || loading || saving || !!editing}
                    onClick={() => openForm(row)}
                  >
                    Открыть форму
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !rows.length && (
          <Text p={6}>Нет записей по заданным условиям.</Text>
        )}
      </Box>
      <Flex mt={3} gap={3} align="center" wrap="wrap">
        <Button
          size="sm"
          isDisabled={page === 1 || loading || busy}
          onClick={() => setPage((p) => p - 1)}
        >
          Назад
        </Button>
        <Text fontSize="sm">
          Страница {page} из {Math.max(1, Math.ceil(total / view.pageSize))} ·
          Всего {total}
        </Text>
        <Button
          size="sm"
          isDisabled={page * view.pageSize >= total || loading || busy}
          onClick={() => setPage((p) => p + 1)}
        >
          Далее
        </Button>
        <Select
          size="sm"
          w="150px"
          aria-label="Строк на странице"
          value={view.pageSize}
          isDisabled={busy}
          onChange={(e) => {
            setPage(1);
            setView({ ...view, pageSize: Number(e.target.value) });
          }}
        >
          {[25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n} строк
            </option>
          ))}
        </Select>
      </Flex>
    </Box>
  );
}
